import { Component, type ReactNode } from 'react';
import { isRouteChunkLoadError } from '@/lib/routeChunkLoadError';

/**
 * Erros de importação dinâmica podem ocorrer ao navegar com chunks de um deploy
 * anterior. Não recarregar automaticamente: o usuário pode estar gravando áudio.
 */
export function RouteLoadingFallback() {
  return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center px-6" role="status" aria-live="polite">
      <div className="text-center">
        <i className="ri-loader-4-line text-4xl text-[#16A34A] animate-spin" aria-hidden="true" />
        <p className="text-[#374151] mt-4 text-sm">Carregando tela...</p>
      </div>
    </div>
  );
}

interface BoundaryState {
  failed: boolean;
  failedToLoadModule: boolean;
}

export class RouteLoadBoundary extends Component<{ children: ReactNode }, BoundaryState> {
  state: BoundaryState = { failed: false, failedToLoadModule: false };

  static getDerivedStateFromError(error: unknown): BoundaryState {
    return { failed: true, failedToLoadModule: isRouteChunkLoadError(error) };
  }

  componentDidCatch(error: Error) {
    // Não registrar mensagem/stack, URL, consulta, token ou dados clínicos.
    console.error(JSON.stringify({
      event: 'VETAI_ROUTE_RENDER_FAILURE',
      kind: isRouteChunkLoadError(error) ? 'chunk_load' : 'render',
    }));
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center px-6" role="alert">
        <div className="max-w-md text-center">
          <i className="ri-error-warning-line text-4xl text-[#16A34A]" aria-hidden="true" />
          <h1 className="mt-4 text-xl font-semibold text-[#111827]">Não foi possível carregar esta tela</h1>
          <p className="mt-3 text-sm text-[#4B5563]">
            {this.state.failedToLoadModule
              ? 'Uma atualização do sistema ou uma falha de conexão impediu o carregamento da página.'
              : 'Ocorreu um erro ao abrir a página. Suas informações já salvas permanecem no sistema.'}
          </p>
          <p className="mt-3 text-xs text-[#6B7280]">
            Se houver gravação ou envio de áudio em andamento, evite atualizar antes de preservar o arquivo.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-6 rounded-lg bg-[#16A34A] px-5 py-3 text-sm font-semibold text-white"
          >
            Recarregar tela
          </button>
        </div>
      </div>
    );
  }
}
